'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

export default function FounderMessageSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-24 bg-background relative overflow-hidden" ref={ref}>
      {/* Subtle warm texture */}
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #c9a84c 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-14 xl:px-20">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-14 section-reveal">
          <div className="h-px w-12 bg-accent/60" />
          <span className="font-sans text-[0.65rem] font-semibold tracking-widest uppercase text-accent/80">From the Founder</span>
          <div className="h-px w-12 bg-accent/60" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait column */}
          <div className="lg:col-span-4 stagger-child">
            <div className="relative rounded-2xl overflow-hidden luxury-shadow-lg aspect-[3/4] max-w-xs mx-auto lg:mx-0" style={{ background: 'linear-gradient(135deg, #1a2b22 0%, #22382c 100%)' }}>
              <Image
                src="/assets/images/Facetune_22-12-2025-11-03-20-1781815665221.jpeg"
                alt="Nomsa Nhlabathi — Founder & Executive Director of REUSA"
                fill
                className="object-cover object-top"
                sizes="(max-width: 1024px) 320px, 380px"
              />
              {/* Name overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-primary/90 backdrop-blur-sm px-6 py-4">
                <p className="font-serif font-semibold text-white text-lg leading-tight">Nomsa Nhlabathi</p>
                <p className="font-sans text-accent text-xs tracking-wide mt-1">Founder & Executive Director</p>
              </div>
            </div>

            {/* Credentials */}
            <div className="mt-6 space-y-2.5 max-w-xs mx-auto lg:mx-0">
              {[
                '12+ Years in South African Real Estate',
                'Founder, Prestige Property Group',
                'Executive Board Member, SAYIPA',
                'Director of Stakeholder Relations',
                'MBA Candidate',
              ]?.map((cred) => (
                <div key={cred} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                  <span className="font-sans text-xs text-muted-foreground leading-relaxed">{cred}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Message column */}
          <div className="lg:col-span-8 stagger-child" style={{ transitionDelay: '120ms' }}>
            <h2 className="font-serif text-foreground mb-8" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}>
              A Message From<br />
              <span className="italic text-gradient-gold">Our Founder</span>
            </h2>

            {/* Quote block */}
            <div className="border-l-2 border-accent pl-7 mb-8">
              <div className="space-y-5">
                <p className="font-serif italic text-lg text-foreground leading-relaxed">
                  &ldquo;REUSA was born from a simple observation: the real estate industry lacks a clear, accessible pathway for aspiring professionals to gain practical, industry-relevant education.
                </p>
                <p className="font-sans text-base text-muted-foreground leading-relaxed">
                  After more than a decade in real estate, I have seen talented individuals enter the profession with ambition but without the support, mentorship and education needed to thrive.
                </p>
                <p className="font-sans text-base text-muted-foreground leading-relaxed">
                  REUSA exists to change that.
                </p>
                <p className="font-sans text-base text-muted-foreground leading-relaxed">
                  Our mission is to create a new generation of ethical, capable and industry-ready property professionals who will shape the future of African real estate.
                </p>
                <p className="font-serif italic text-base text-foreground leading-relaxed">
                  Whether you are starting your career, growing your business or advancing into leadership, REUSA was built with you in mind.&rdquo;
                </p>
              </div>
            </div>

            {/* Signature */}
            <div className="flex items-center gap-4 mb-10">
              <div>
                <p className="font-serif font-semibold text-foreground text-base">— Nomsa Nhlabathi</p>
                <p className="font-sans text-sm text-accent tracking-wide">Founder & Executive Director, REUSA</p>
              </div>
            </div>

            {/* CTA */}
            <Link
              href="/founders-letter"
              className="inline-flex items-center gap-2 font-sans text-xs font-semibold tracking-widest uppercase text-primary hover:text-accent transition-colors border-b border-primary/30 hover:border-accent pb-0.5"
              style={{ letterSpacing: '0.1em' }}
            >
              Read the Full Founder&apos;s Letter
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
